import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { renderToStaticMarkup } from 'react-dom/server';
import cx from 'classnames';
import OrderProp from '../../propTypes/orderProp';
import translate from '../../../../commonComponents/translate';
import {
  compose,
  findCurrencySymbol,
  formatNumbers,
  formatPrice,
  resolveDocumentUrl,
} from '../../../../services/utilityFunctions';
import List from '../../../../commonComponents/lists/ClientSideList';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import CurrencyProp from '../../../profile/propTypes/currencyProp';
import SummaryContainer from './SummaryContainer';
import InfoTabHeader from './InfoTabHeader';
import User from '../../../../../../../domain/models/user';
import FormButton from '../../../../commonComponents/buttons/FormButton';
import { useModal } from '../../../../services/customHooks';
import FormModal from '../../../../commonComponents/modal/FormModal';
import { PROVIDER_TYPE } from '../../../../commonComponents/propTypes/marketProductForm';
import PdfFormModal from '../../../../commonComponents/modal/PdfFormModal';
import ProductStatus from '../../../orders/Components/helpers/ProductStatus';
import {
  isAbleToSendOfferEmail,
  sendOfferEmail,
} from '../../../opportunities/components/helpers/opportunitiesListHelper';
import withCSRF from '../../../../commonComponents/withCSRF';
import GenericModal from '../../../../commonComponents/modal/GenericModal';
import { PACK_TYPE } from '../../propTypes/productProp';

const productIsPack = product =>
  product.type === 'pack' || product?.type === 'pack_advanced';

const renderDigitalContent = product => {
  const { digitalDocument = [] } = product || {};
  if (!digitalDocument || digitalDocument?.length === 0) {
    return <div>-</div>;
  }
  if (digitalDocument.length === 1) {
    return (
      <div>
        {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
        <a
          href={resolveDocumentUrl(digitalDocument[0].url)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <i className="la la-download" />
        </a>
      </div>
    );
  }

  return (
    <div>
      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
      <button
        type="button"
        className="open-digital"
        data-files={JSON.stringify(product.digitalDocument)}
      >
        <i className="la la-download" />
      </button>
    </div>
  );
};

const renderPackName = (product, pack, t) => (
  <div className="variationName-column">
    <img src={pack?.images[0]?.thumb} alt={pack?.name} />
    <div className="variation-info">
      <div className="product-title-list">
        <span className="pack-badge">
          {product?.type === 'pack_advanced'
            ? t('shop.advancedPack')
            : t('shop.order_pack')}
        </span>
        <span className="information">{pack?.name}</span>
        <span className="pack-contents-count">{`${
          product?.type === 'pack_advanced'
            ? product?.packProduct?.pack?.amountVariationsPerAdvancedPack
            : product.packVariations.length
        } ${t('shop.order_products_inside')}`}</span>
      </div>
      <ProductStatus t={t} status={pack?.pack?.status} />
    </div>
  </div>
);

const renderProductName = (productParam, t) => {
  const {
    parentProduct,
    packProduct,
    product: variationProduct,
  } = productParam;
  if (productIsPack(productParam)) {
    return renderPackName(productParam, packProduct, t);
  }
  const isHybridPack = parentProduct?.pack?.packType === PACK_TYPE.HYBRID_PACK;
  return (
    <div className="variationName-column">
      <img src={variationProduct?.icon?.thumb} alt={variationProduct?.name} />
      <div className="variation-info">
        <div
          style={
            isHybridPack ? { display: 'flex', flexDirection: 'column' } : {}
          }
        >
          {isHybridPack && (
            <span className="pack-badge">{t('shop.hybrid_pack')}</span>
          )}
          <span className="information">
            {' '}
            {`${parentProduct?.name}: ${variationProduct?.name}`}
          </span>
        </div>
        <ProductStatus t={t} status={variationProduct?.status} />
      </div>
    </div>
  );
};

function InfoTab(props) {
  const {
    t,
    _csrf,
    order,
    isAcademy,
    currencies,
    isOpportunity,
    onCreditNotesClick,
  } = props;

  const { tenant } = useContext(ConfigsContext);
  const { loggedUser } = useContext(LoggedUserContext);
  const { directSellingSettings, directSelling } = tenant?.settings || {};
  const { CVIntegration, VAIntegration } = directSellingSettings || {};
  const isOwnerACollaborator =
    loggedUser?.type === User.TYPE.COLLABORATOR ||
    loggedUser?.role === User.ROLE.ADMIN;

  const showVA = directSelling && VAIntegration && isOwnerACollaborator;
  const showCV = directSelling && CVIntegration && isOwnerACollaborator;

  // region Send Offer
  const shouldShow =
    isOpportunity &&
    isAbleToSendOfferEmail(order?.status) &&
    loggedUser?._id === order?.seller?._id;
  const [isLoading, setIsLoading] = useState(false);
  const [digitalDocsModal, setDigitalDocsModal] = useModal({
    show: false,
    data: null,
  });

  const handleSendOffer = useCallback(async () => {
    await sendOfferEmail(_csrf, [order?._id], updatedIsLoading =>
      setIsLoading(updatedIsLoading),
    );
  }, [_csrf, setIsLoading, sendOfferEmail]);

  const sendOfferBtnClass = cx(
    'btn',
    'btn-dynamic',
    'm-btn--pill',
    'btn-sendOffer',
    { 'm-loader': isLoading },
    { 'm-loader--right': isLoading },
  );

  // endregion Send Offer

  const tableId = 'productsList';

  // region Form Popup
  const [formModal, setFormModal] = useModal({
    show: false,
    data: null,
  });

  const handleFormSave = () => {
    setFormModal({
      show: false,
      data: null,
    });
  };

  const { products } = order;

  const hasDigitalProduct = useMemo(
    () =>
      isAcademy
        ? null
        : products.find(p => p.product && p.product.digitalDocument !== null),
    [isAcademy],
  );
  const hasFormProduct = useMemo(
    () => products.find(p => p.formAnswerData),
    [],
  );

  function handleListInitialization() {
    const $tableSelector = $(`#${tableId}`);
    $tableSelector.on('click', '.btn-fill-form', function () {
      const $btn = $(this);
      const productId = $btn.data('product');

      const product = products.find(p => p._id === productId);
      const isSurveyJSForm =
        product?.product?.form?.providerType === PROVIDER_TYPE.SURVEY_JS;

      const formToModal = isSurveyJSForm
        ? {
            productId,
            formQuestionData: product?.formQuestionsData,
            formAnswerData: product?.formAnswerData || null,
            providerType: product?.product?.form?.providerType,
          }
        : {
            productId,
            providerData: product?.formAnswerData || null,
            formAnswersLink: product?.formAnswersLink || null,
            templatePDF: product?.product?.form?.templatePDF,
            productForm: product?.product?.form,
            providerType: product?.product?.form?.providerType,
          };

      setFormModal({
        show: true,
        data: formToModal,
      });
    });

    $tableSelector.on('click', '.open-digital', function () {
      const $btn = $(this);
      const files = $btn.data('files');
      setDigitalDocsModal({
        show: true,
        data: files || [],
      });
    });
  }

  const columns = useMemo(() => {
    let columnsParam;

    if (!isAcademy) {
      columnsParam = [
        {
          field: '_id',
          width: 10,
          textAlign: 'left',
          title: '',
          sortable: false,
          overflow: 'visible',
        },
        {
          field: 'Name',
          width: 200,
          textAlign: 'left',
          title: t('shop.order_product_name'),
          sortable: false,
          overflow: 'visible',
          template: product =>
            renderToStaticMarkup(renderProductName(product, t)),
        },
        {
          field: 'cost',
          title: t('shop.order.detail.unitCost'),
          textAlign: 'left',
          width: 80,
          sortable: false,
          template: product =>
            formatPrice(
              product.price,
              findCurrencySymbol(order.currency, currencies),
            ),
        },
        {
          field: 'quantity',
          title: t('shop.order_product_quantity'),
          textAlign: 'left',
          width: 80,
          template: product => product.quantity,
        },
        {
          field: 'vat',
          title: t('shop.order.pay.estimation.tax'),
          textAlign: 'left',
          width: 80,
          template: product => `${product.tax}%`,
        },
      ];
    } else {
      columnsParam = [
        {
          field: '_id',
          width: 10,
          textAlign: 'left',
          title: '',
          sortable: false,
          overflow: 'visible',
        },
        {
          field: 'type',
          title: t('academy.order.type'),
          textAlign: 'left',
          width: 80,
          template: ({ type }) => renderToStaticMarkup(<>{t(type)}</>),
        },
        {
          field: 'name',
          title: t('shop.order_product_name'),
          textAlign: 'name',
          width: 80,
          template: ({ name }) => name,
        },
        {
          field: 'cost',
          title: t('shop.order.detail.unitCost'),
          textAlign: 'left',
          width: 80,
          sortable: false,
          template: product =>
            formatPrice(
              product.price,
              findCurrencySymbol(order.currency, currencies),
            ),
        },
        {
          field: 'quantity',
          title: t('shop.order_product_quantity'),
          textAlign: 'left',
          width: 80,
          template: product => product.quantity,
        },
        {
          field: 'vat',
          title: t('shop.order.pay.estimation.tax'),
          textAlign: 'left',
          width: 80,
          template: product => `${product.tax}%`,
        },
      ];
    }

    if (showVA) {
      columnsParam.push({
        field: 'va',
        title: t('commissions.va'),
        textAlign: 'left',
        width: 60,
        template: ({ va }) => (va ? formatNumbers(va, false, true) : '-'),
      });
    }

    if (showCV) {
      columnsParam.push({
        field: 'cv',
        title: t('commissions.cv'),
        textAlign: 'center',
        width: 60,
        template: ({ cv }) => (cv ? formatNumbers(cv, false, true) : '-'),
      });
    }

    columnsParam.push({
      field: 'total',
      title: t('order_item_subtotal'),
      textAlign: 'left',
      width: 80,
      template: product =>
        formatPrice(
          product.subtotal,
          findCurrencySymbol(order.currency, currencies),
        ),
    });

    // if order contain digital product, show the column
    if (hasDigitalProduct) {
      columnsParam.push({
        field: 'digitalProduct',
        title: t('shop.order.detail.digitalContent'),
        textAlign: 'left',
        width: 80,
        template: ({ product }) =>
          renderToStaticMarkup(renderDigitalContent(product)),
      });
    }

    if (isOpportunity && hasFormProduct) {
      columnsParam.push({
        field: 'formBtn',
        title: '',
        textAlign: 'left',
        width: 150,
        template: product =>
          renderToStaticMarkup(
            product?.form ? <FormButton product={product} t={t} /> : null,
          ),
      });
    }
    return columnsParam;
  }, []);

  const packVariationsColumns = useMemo(
    () => [
      {
        field: 'name',
        width: 300,
        textAlign: 'left',
        title: t('shop.order_product_name'),
        sortable: false,
        overflow: 'visible',
        template: product =>
          renderToStaticMarkup(
            <div className="variationName-column">
              <img
                src={product?.variation?.icon?.thumb}
                alt={product?.variation?.name}
              />
              <div className="variation-info">
                <span className="information">{`${product?.variation?.name}`}</span>
              </div>
            </div>,
          ),
      },
      {
        field: 'quantity',
        title: t('shop.order_product_quantity'),
        textAlign: 'center',
        width: 100,
        sortable: false,
        template: product =>
          renderToStaticMarkup(<span>{product?.quantity || 1}</span>),
      },
    ],
    [],
  );

  const packSubTable = row => {
    const packVariations = row?.data?.packVariations || [];
    if (packVariations.length === 0) return;

    const config = {
      data: {
        type: 'local',
        source: packVariations,
        pageSize: packVariations.length,
      },
      layout: {
        theme: 'default',
        spinner: {
          type: 1,
          theme: 'default',
        },
      },
      pagination: false,
      columns: packVariationsColumns.map(col => ({
        ...col,
        template: variation => col.template(variation),
      })),
    };

    $('<div/>')
      .attr('id', `pack_variations_${row?.data?._id}`)
      .appendTo(row.detailCell)
      .mDatatable(config);
  };

  useEffect(() => {
    const $productsList = $('#productsList');
    $productsList.find('.m-datatable__row').each(function () {
      const $row = $(this);
      const productId = $row
        .find('.m-datatable__toggle-subtable')
        .attr('data-value');
      const product = products.find(p => p._id === productId);

      if (product && !productIsPack(product)) {
        $row.find('.m-datatable__toggle-subtable').remove();
      }
    });
  }, [products]);

  return (
    <>
      {formModal.show ? (
        <>
          {formModal?.data?.providerType === PROVIDER_TYPE.SURVEY_JS ? (
            <FormModal
              form={formModal.data}
              onClose={handleFormSave}
              isDisabled
            />
          ) : (
            <PdfFormModal
              form={formModal.data}
              onClose={handleFormSave}
              isDisabled
              opportunity={order}
            />
          )}
        </>
      ) : null}

      {digitalDocsModal.show && (
        <GenericModal
          modalId="notLoggedModal"
          title={t('shop.download')}
          message={
            <div className="download">
              <List
                data={digitalDocsModal.data}
                columns={[
                  {
                    field: 'originalName',
                    textAlign: 'center',
                    title: t('file'),
                    width: 200,
                    sortable: false,
                  },
                  {
                    field: 'file',
                    textAlign: 'center',
                    title: '',
                    width: 200,
                    sortable: false,
                    template: ({ url }) =>
                      renderToStaticMarkup(
                        <a
                          href={resolveDocumentUrl(url)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="la la-download" />
                        </a>,
                      ),
                  },
                ]}
                id="downloadFilesModal"
                loading={false}
              />
            </div>
          }
          onClose={() => setDigitalDocsModal({ show: false, data: null })}
          saveMessage={t('close')}
        />
      )}

      <div className="m-portlet m-portlet-rounded info-wrapper">
        <InfoTabHeader
          order={order}
          isAcademy={isAcademy}
          isOpportunity={isOpportunity}
        />
        <div>
          <List
            id={tableId}
            loading={false}
            data={products}
            columns={columns}
            pagination={false}
            onInit={handleListInitialization}
            detail={{
              title: t('catalog_products_inside'),
              content: row => {
                if (productIsPack(row.data)) {
                  packSubTable(row);
                }
              },
            }}
          />
          <div className="btn-opportunity-wrapper">
            {shouldShow && (
              <button
                type="button"
                onClick={handleSendOffer}
                className={sendOfferBtnClass}
                disabled={isLoading}
              >
                <p>{t('send.offer.email')}</p>
                {!isLoading && <i className="fa fa-paper-plane" />}
              </button>
            )}
          </div>
        </div>
        <SummaryContainer
          order={order}
          isAcademy={isAcademy}
          isOpportunity={isOpportunity}
          onCreditNotesClick={onCreditNotesClick}
        />
      </div>
    </>
  );
}

InfoTab.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  order: PropTypes.shape(OrderProp.propType).isRequired,
  isAcademy: PropTypes.bool,
  isOpportunity: PropTypes.bool,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType)),
  onCreditNotesClick: PropTypes.func.isRequired,
};

InfoTab.defaultProps = {
  isAcademy: false,
  isOpportunity: false,
  currencies: [],
};

export default compose(withCSRF, translate)(InfoTab);
